import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import { Add, CalendarToday, Download, LocalOffer, NoteAdd, PersonAdd, Print, Refresh } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import type { Dayjs } from "dayjs"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { useBatchTransitionOrdersMutation, useBulkUpdateOrdersMutation, useGetTagsQuery } from "../../apis/appApi"
import { AssignCourierDialog } from "./AssignCourierDialog"
import { TagSelector } from "./create/TagSelector"
import { ActionButton } from "./ordersPageConfig"

/** Groups where bulk assign is available (pre-completion states) */
const ASSIGN_VISIBLE_GROUPS = [
  "inPreparation",
  "readyForHaulage",
  "onTheWay",
  "actionRequired",
]

type OrderActionBarProps = {
  filteredCount: number
  groupCount: number
  groupLabel: string
  actions: ActionButton[]
  selectedGroup: string | null
  selectedOrderIds: number[]
  onRefresh: () => void
  onClearSelection: () => void
  selectedDate: Dayjs
  totalDayCount: number
  groupColor: string
  onCreateOrder: () => void
  isMobile: boolean
  onExport: () => void
  isExporting: boolean
  onPrintLabels: () => void
}

export const OrderActionBar: FC<OrderActionBarProps> = ({
  filteredCount,
  groupCount,
  groupLabel,
  actions,
  selectedGroup,
  selectedOrderIds,
  onRefresh,
  onClearSelection,
  selectedDate,
  totalDayCount,
  groupColor,
  onCreateOrder,
  isMobile,
  onExport,
  isExporting,
  onPrintLabels,
}) => {
  const { t } = useTranslation()
  const [confirmAction, setConfirmAction] = useState<ActionButton | null>(null)
  const [operatorCode, setOperatorCode] = useState("")
  const [transitionMessage, setTransitionMessage] = useState("")
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: "success" | "error"
  }>({ open: false, message: "", severity: "success" })

  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [notesDialogOpen, setNotesDialogOpen] = useState(false)
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false)
  const [bulkNotes, setBulkNotes] = useState("")
  const [bulkTagIds, setBulkTagIds] = useState<number[]>([])

  const [batchTransition, { isLoading }] = useBatchTransitionOrdersMutation()
  const [bulkUpdate, { isLoading: isBulkUpdating }] = useBulkUpdateOrdersMutation()
  const { data: orderTags } = useGetTagsQuery({ type: "ORDER" })

  const visibleActions = actions.filter(
    (a) => selectedGroup && a.visibleForGroups.includes(selectedGroup),
  )

  const showAssignButton =
    selectedOrderIds.length > 0 &&
    selectedGroup != null &&
    ASSIGN_VISIBLE_GROUPS.includes(selectedGroup)

  const handleAssignSuccess = (succeeded: number, failed: number) => {
    if (failed === 0) {
      setSnackbar({
        open: true,
        message: t("ordersPage.assignCourier.success", { count: succeeded }),
        severity: "success",
      })
    } else if (succeeded === 0) {
      setSnackbar({
        open: true,
        message: t("ordersPage.assignCourier.error"),
        severity: "error",
      })
    } else {
      setSnackbar({
        open: true,
        message: t("ordersPage.assignCourier.partial", { succeeded, failed }),
        severity: "error",
      })
    }
    onClearSelection()
  }

  const handleBulkNotesConfirm = async () => {
    if (!bulkNotes.trim() || selectedOrderIds.length === 0) return
    try {
      const result = await bulkUpdate({
        orderIds: selectedOrderIds,
        notes: bulkNotes.trim(),
      }).unwrap()
      const succeeded = result.results.filter((r) => r.success).length
      setSnackbar({
        open: true,
        message: t("ordersPage.bulkNotesAdded", { count: succeeded }),
        severity: "success",
      })
    } catch {
      setSnackbar({
        open: true,
        message: t("ordersPage.batchError"),
        severity: "error",
      })
    }
    onClearSelection()
    setNotesDialogOpen(false)
    setBulkNotes("")
  }

  const handleBulkTagsConfirm = async () => {
    if (bulkTagIds.length === 0 || selectedOrderIds.length === 0) return
    try {
      const result = await bulkUpdate({
        orderIds: selectedOrderIds,
        tagIds: bulkTagIds,
      }).unwrap()
      const succeeded = result.results.filter((r) => r.success).length
      setSnackbar({
        open: true,
        message: t("ordersPage.bulkTagsAdded", { count: succeeded }),
        severity: "success",
      })
    } catch {
      setSnackbar({
        open: true,
        message: t("ordersPage.batchError"),
        severity: "error",
      })
    }
    onClearSelection()
    setTagsDialogOpen(false)
    setBulkTagIds([])
  }

  const handleConfirm = async () => {
    if (!confirmAction || selectedOrderIds.length === 0 || !operatorCode.trim()) return

    try {
      const result = await batchTransition({
        orderIds: selectedOrderIds,
        event: confirmAction.event,
        operatorCode: operatorCode.trim(),
        ...(transitionMessage.trim() ? { message: transitionMessage.trim() } : {}),
      }).unwrap()

      const succeeded = result.results.filter((r) => r.success).length
      const failed = result.results.filter((r) => !r.success).length

      if (failed === 0) {
        setSnackbar({
          open: true,
          message: t("ordersPage.batchSuccess", { count: succeeded }),
          severity: "success",
        })
      } else {
        setSnackbar({
          open: true,
          message: t("ordersPage.batchPartial", {
            succeeded,
            failed,
          }),
          severity: "error",
        })
      }
    } catch {
      setSnackbar({
        open: true,
        message: t("ordersPage.batchError"),
        severity: "error",
      })
    }

    onClearSelection()
    setConfirmAction(null)
    setOperatorCode("")
    setTransitionMessage("")
  }

  const batchButtons = visibleActions.map((action) => (
    <Button
      data-testid={`orders-action-${action.key}-btn`}
      key={action.key}
      size="small"
      variant="outlined"
      color={action.color}
      disabled={selectedOrderIds.length === 0 || isLoading}
      onClick={() => setConfirmAction(action)}
    >
      {t(action.labelKey)}
      {selectedOrderIds.length > 0 && ` (${selectedOrderIds.length})`}
    </Button>
  ))

  return (
    <>
      <Box
        {...uiSurface("UI-DLV2-024")}
        data-testid="orders-action-bar"
        sx={{
          px: { xs: 2, md: 3 },
          pt: 2,
          pb: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        {isMobile ? (
          /* ── Mobile layout ── */
          <Stack spacing={1}>
            {/* Row 1: Title + New Order + Refresh */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" fontWeight={600}>
                {t("orders.title")}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Button
                  data-testid="orders-create-btn"
                  size="small"
                  variant="contained"
                  startIcon={<Add />}
                  onClick={onCreateOrder}
                >
                  {t("orders.create")}
                </Button>
                <IconButton
                  data-testid="orders-export-btn-mobile"
                  size="small"
                  onClick={onExport}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Download fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  data-testid="orders-refresh-btn"
                  size="small"
                  onClick={onRefresh}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>

            {/* Row 2: Date chip + Group chip + count */}
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Chip
                icon={<CalendarToday sx={{ fontSize: 16 }} />}
                label={selectedDate.format("MMM D")}
                variant="outlined"
                size="small"
              />
              <Chip
                label={groupLabel}
                size="small"
                sx={{
                  bgcolor: groupColor,
                  color: "#fff",
                  fontWeight: 500,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {groupCount} {t("ordersPage.ordersCount")}
              </Typography>
            </Stack>

            {/* Row 3: Batch actions — only when selected */}
            {selectedOrderIds.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {batchButtons}
                {showAssignButton && (
                  <Button
                    data-testid="orders-assign-courier-btn-mobile"
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<PersonAdd />}
                    onClick={() => setAssignDialogOpen(true)}
                  >
                    {t("ordersPage.actions.assignCourier")} ({selectedOrderIds.length})
                  </Button>
                )}
                <Button
                  data-testid="orders-bulk-notes-btn-mobile"
                  size="small"
                  variant="outlined"
                  startIcon={<NoteAdd />}
                  disabled={isBulkUpdating}
                  onClick={() => setNotesDialogOpen(true)}
                >
                  {t("ordersPage.bulkAddNotes")} ({selectedOrderIds.length})
                </Button>
                <Button
                  data-testid="orders-bulk-tags-btn-mobile"
                  size="small"
                  variant="outlined"
                  startIcon={<LocalOffer />}
                  disabled={isBulkUpdating}
                  onClick={() => setTagsDialogOpen(true)}
                >
                  {t("ordersPage.bulkAddTags")} ({selectedOrderIds.length})
                </Button>
                <Button
                  data-testid="orders-print-labels-btn-mobile"
                  size="small"
                  variant="outlined"
                  startIcon={<Print />}
                  onClick={onPrintLabels}
                >
                  {t("print.printLabels")} ({selectedOrderIds.length})
                </Button>
              </Stack>
            )}
          </Stack>
        ) : (
          /* ── Desktop layout ── */
          <Stack spacing={0.5}>
            {/* Row 1: Title + day count | batch buttons + refresh */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="baseline" spacing={1.5}>
                <Typography variant="h5" fontWeight={600}>
                  {t("orders.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("ordersPage.totalDayCount", { count: totalDayCount })}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                {batchButtons}
                {showAssignButton && (
                  <Tooltip title={t("ordersPage.actions.assignCourier")}>
                    <Button
                      data-testid="orders-assign-courier-btn"
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<PersonAdd />}
                      onClick={() => setAssignDialogOpen(true)}
                    >
                      {t("ordersPage.actions.assignCourier")} ({selectedOrderIds.length})
                    </Button>
                  </Tooltip>
                )}
                {selectedOrderIds.length > 0 && (
                  <Tooltip title={t("ordersPage.bulkAddNotes")}>
                    <Button
                      data-testid="orders-bulk-notes-btn"
                      size="small"
                      variant="outlined"
                      startIcon={<NoteAdd />}
                      disabled={isBulkUpdating}
                      onClick={() => setNotesDialogOpen(true)}
                    >
                      {t("ordersPage.bulkAddNotes")} ({selectedOrderIds.length})
                    </Button>
                  </Tooltip>
                )}
                {selectedOrderIds.length > 0 && (
                  <Tooltip title={t("ordersPage.bulkAddTags")}>
                    <Button
                      data-testid="orders-bulk-tags-btn"
                      size="small"
                      variant="outlined"
                      startIcon={<LocalOffer />}
                      disabled={isBulkUpdating}
                      onClick={() => setTagsDialogOpen(true)}
                    >
                      {t("ordersPage.bulkAddTags")} ({selectedOrderIds.length})
                    </Button>
                  </Tooltip>
                )}
                {selectedOrderIds.length > 0 && (
                  <Tooltip title={t("print.printLabels")}>
                    <Button
                      data-testid="orders-print-labels-btn"
                      size="small"
                      variant="outlined"
                      startIcon={<Print />}
                      onClick={onPrintLabels}
                    >
                      {t("print.printLabels")} ({selectedOrderIds.length})
                    </Button>
                  </Tooltip>
                )}
                <Box sx={{ ml: 1 }}>
                  <Tooltip title={t("ordersPage.export")}>
                    <span>
                      <IconButton
                        data-testid="orders-export-btn"
                        size="small"
                        onClick={onExport}
                        disabled={isExporting}
                      >
                        {isExporting ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Download fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    data-testid="orders-refresh-btn"
                    size="small"
                    onClick={onRefresh}
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </Box>
              </Stack>
            </Stack>

            {/* Row 2: Group label + filtered count */}
            <Typography
              data-testid="orders-count-label"
              variant="body2"
              color="text.secondary"
            >
              {groupLabel} ({groupCount})
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Confirmation dialog */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-029") }}
        data-testid="orders-confirm-dialog"
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>{t("ordersPage.confirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2.5 }}>
            {t("ordersPage.confirmMessage", {
              action: confirmAction ? t(confirmAction.labelKey) : "",
              count: selectedOrderIds.length,
            })}
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              label={t("ordersPage.employeeCode")}
              required
              fullWidth
              value={operatorCode}
              onChange={(e) => setOperatorCode(e.target.value)}
              placeholder={t("ordersPage.employeeCodePlaceholder")}
              error={!operatorCode.trim()}
              helperText={!operatorCode.trim() ? t("ordersPage.employeeCodeRequired") : ""}
              autoFocus
            />
            <TextField
              label={t("ordersPage.transitionMessage")}
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              value={transitionMessage}
              onChange={(e) => setTransitionMessage(e.target.value)}
              placeholder={t("ordersPage.transitionMessagePlaceholder")}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            data-testid="orders-confirm-cancel-btn"
            onClick={() => { setConfirmAction(null); setOperatorCode(""); setTransitionMessage("") }}
            disabled={isLoading}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="orders-confirm-submit-btn"
            onClick={handleConfirm}
            variant="contained"
            color={confirmAction?.color ?? "primary"}
            disabled={isLoading || !operatorCode.trim()}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Courier dialog (F064) */}
      <AssignCourierDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        orderIds={selectedOrderIds}
        onSuccess={handleAssignSuccess}
      />

      {/* Bulk Add Notes dialog (F065) */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-031") }}
        data-testid="orders-bulk-notes-dialog"
        open={notesDialogOpen}
        onClose={() => { setNotesDialogOpen(false); setBulkNotes("") }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("ordersPage.bulkAddNotes")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("ordersPage.confirmMessage", {
              action: t("ordersPage.bulkAddNotes"),
              count: selectedOrderIds.length,
            })}
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            value={bulkNotes}
            onChange={(e) => setBulkNotes(e.target.value)}
            placeholder={t("ordersPage.bulkNotesPlaceholder")}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => { setNotesDialogOpen(false); setBulkNotes("") }}
            disabled={isBulkUpdating}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="orders-bulk-notes-confirm-btn"
            onClick={handleBulkNotesConfirm}
            variant="contained"
            disabled={isBulkUpdating || !bulkNotes.trim()}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Add Tags dialog (F065) */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-032") }}
        data-testid="orders-bulk-tags-dialog"
        open={tagsDialogOpen}
        onClose={() => { setTagsDialogOpen(false); setBulkTagIds([]) }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("ordersPage.bulkAddTags")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("ordersPage.confirmMessage", {
              action: t("ordersPage.bulkAddTags"),
              count: selectedOrderIds.length,
            })}
          </DialogContentText>
          <TagSelector
            selectedTagIds={bulkTagIds}
            availableTags={orderTags ?? []}
            onChange={(tagId) => {
              setBulkTagIds((prev) =>
                prev.includes(tagId)
                  ? prev.filter((id) => id !== tagId)
                  : [...prev, tagId],
              )
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => { setTagsDialogOpen(false); setBulkTagIds([]) }}
            disabled={isBulkUpdating}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="orders-bulk-tags-confirm-btn"
            onClick={handleBulkTagsConfirm}
            variant="contained"
            disabled={isBulkUpdating || bulkTagIds.length === 0}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Result snackbar */}
      <Snackbar
        data-testid="orders-snackbar"
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}
